import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, ExternalLink } from "lucide-react";
import { PLATFORMS } from "../data/platforms";

const MENU = PLATFORMS.filter((p) => p.slug !== "foresightrx");

export function PlatformsNav() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  const active = pathname.startsWith("/platforms");

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); ref.current?.querySelector("button")?.focus(); } };
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey); document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open]);

  return (
    <div ref={ref} className="relative flex items-center">
      <Link to="/platforms" data-testid="nav-platforms" className={`rounded-md py-2 pl-3 pr-1 text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--teal)] ${active ? "text-[var(--teal)]" : "text-slate-700 hover:text-[var(--teal)]"}`}>Platforms</Link>
      <button type="button" data-testid="nav-platforms-toggle" aria-label="Show platforms menu" aria-expanded={open} aria-haspopup="true" aria-controls="platforms-menu" onClick={() => setOpen((o) => !o)} className={`rounded-md p-1.5 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--teal)] ${active ? "text-[var(--teal)]" : "text-slate-700 hover:text-[var(--teal)]"}`}>
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div id="platforms-menu" role="menu" data-testid="nav-platforms-menu" className="absolute left-0 top-full z-50 w-64 pt-3">
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
            {MENU.map((p) => (
              <a key={p.slug} role="menuitem" href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`nav-platform-${p.slug}`} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-[var(--navy)] transition-colors duration-150 hover:bg-[var(--surface)] focus-visible:bg-[var(--surface)] focus-visible:outline-none">
                {p.name}<ExternalLink size={13} className="shrink-0 text-slate-400" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PlatformsMobileNav({ onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="nav-mobile-platforms">
      <div className="flex items-center justify-between">
        <Link to="/platforms" onClick={onNavigate} data-testid="nav-mobile-platforms-link" className="block py-2 text-sm font-semibold text-slate-800">Platforms</Link>
        <button type="button" data-testid="nav-mobile-platforms-toggle" aria-label={open ? "Hide platform links" : "Show platform links"} aria-expanded={open} aria-controls="platforms-mobile-menu" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }} className="flex h-11 w-11 items-center justify-center rounded-md text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--teal)]">
          <ChevronDown size={18} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div id="platforms-mobile-menu" className="mb-2 ml-3 border-l-2 border-slate-100 pl-3">
          {MENU.map((p) => (
            <a key={p.slug} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`nav-mobile-platform-${p.slug}`} className="flex items-center justify-between py-2 text-sm font-medium text-slate-700">{p.name}<ExternalLink size={13} className="text-slate-400" aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span></a>
          ))}
        </div>
      )}
    </div>
  );
}
